import { DarkThemeToggle, Tooltip } from "flowbite-react";
import { useCallback, useEffect, useState } from "react";

import { NavLink, useLocation, useNavigate } from "react-router-dom";
import config from "../../config";
import { getDashboardResponse } from "../../services/billing";
import { getNotifications } from "../../services/notification";

import { useAppInfo } from "../../helpers/hooks/common-hook";
import { getMyAccount } from "../../services/account/account-service";
import { getReviewRequest } from "../../services/app/app-service";

import { HiBell, HiOutlineBell } from "react-icons/hi";
import { HiChatBubbleOvalLeftEllipsis, HiOutlineChatBubbleOvalLeftEllipsis } from "react-icons/hi2";
import DashboardRouter from "../dashboard-router";
import CopilotWorkspace from "../copilot_native/copilot-workspace";
import { IconButton, IconHref } from "./components/buttons";
import Header from "./components/header";
import Sidebar, { SidebarSectionLabel } from "./components/sidebar";
import MenuItem from "./components/sidebar/menu-item";
import MenuItemGroup from "./components/sidebar/menu-item-group";
import CopilotLaunch from "./components/sidebar/copilot-launch";
import useResourceFlash from "../copilot_native/hooks/use-resource-flash";
import { navFlashKeys } from "../copilot_native/model/copilot-paths";
import { FaWandMagicSparkles as SparkIcon } from "react-icons/fa6";
import { LuMenu, LuPlayCircle, LuRocket } from "react-icons/lu";

import { Button } from "flowbite-react";
import {
  appNavigation,
  appNavigationEnd,
  appsNavigation,
  groupNavigationBySection,
  NAV_SECTIONS,
  userSettingsNavigation,
} from "./const";
import {
  AGENT_ROOT_NAV_ITEM,
  SUB_AGENTS_NAV_GROUP,
} from "../../consts/agent-nav-config";
import { createAppBasePath } from "../../consts/route-consts";
import TextToAgentModel from "../text-to-ai-agent";
import { APP_MODES } from "../../consts/common-consts";
import ImpersonationBanner from "../../components/impersonation-banner";
import ConversationLimitBanner from "../../components/conversation-limit-banner";
import { isImpersonating } from "../../services/auth/impersonation-service";

export default function DashboardPage() {
  const location = useLocation();
  const appInfo = useAppInfo();
  const navigate = useNavigate();
  // console.log("****appInfo", appInfo);
  const [navigation, setNavigation] = useState([]);
  const [selectedNav, setSelectedNav] = useState(0);
  const [account, setAccount] = useState(null);
  const [billingSubscription, setBillingSubscription] = useState({
    status: "active",
    trialEnd: null,
    totalMessages: 0,
  });

  const [notifications, setNotifications] = useState([]);
  const [notificationsFetchState, setNotificationsFetchState] = useState({
    processing: false,
    success: false,
    failed: false,
    errorMessage: "",
  });
  const [reviewRequestData, setReviewRequestData] = useState(null);

  const [userRole, setUserRole] = useState(null);

  const [openTextToAgentBuild, setOpenTextToAgentBuild] = useState(false);
  // support staff viewing a customer account: read-only, so the copilot (which edits) is hidden
  const [impersonating] = useState(() => isImpersonating());
  const isSimulator = /^\/app\/[^/]+\/agent-preview\/?$/.test(location.pathname);
  const isAppConsole = /^\/app\/[^/]+\/console\/?$/.test(location.pathname);
  const [menuOpen, setMenuOpen] = useState(() => {
    if (isSimulator) return false;
    try { return localStorage.getItem('bc:menu-open') !== '0'; } catch (_) { return true; }
  });
  const [copilotPanelOpen, setCopilotPanelOpen] = useState(false);
  const handleCopilotStepperOpenChange = useCallback(open => {
    if (open) setMenuOpen(false);
  }, []);
  const handleCopilotPanelOpenChange = useCallback(open => {
    setCopilotPanelOpen(open);
    if (open) setMenuOpen(false);
  }, []);
  useEffect(() => {
    try { localStorage.setItem('bc:menu-open', menuOpen ? '1' : '0'); } catch (_) {}
  }, [menuOpen]);

  // Close on entry (including switching apps), while still allowing the user
  // to reopen navigation without it closing on subsequent Simulator renders.
  useEffect(() => {
    if (isSimulator) setMenuOpen(false);
  }, [isSimulator, location.pathname]);

  // Nav items the copilot just changed blink for a moment.
  const flashingNav = useResourceFlash(navFlashKeys);
  const updateFormState = (processing, success, failed, errorMessage) => {
    setNotificationsFetchState(
      Object.assign({}, { processing, success, failed, errorMessage })
    );
  };

  async function fetchNotifications() {
    updateFormState(true, false, false);
    const { status, response } = await getNotifications();
    if (status === 200) {
      const { data, pagination } = response;
      setNotifications(data);
    }

    updateFormState(false, false, false);
  }

  async function fetchMyAccount() {
    updateFormState(true, false, false);
    const { account } = await getMyAccount();
    setAccount(account || null);

    setUserRole(account?.role || null);

    console.log('User role set to:', account);

    updateFormState(false, false, false);
  }

  async function fetchReviewRequest() {
    if (appInfo && appInfo.appId) {
      const reviewRequest = await getReviewRequest(appInfo.appId);
      setReviewRequestData(reviewRequest);
    } else {
      setReviewRequestData(null);
    }
  }

  async function fetchSubscription() {
    try {
      updateFormState(true, false, false);
      const { response } = await getDashboardResponse();
      if (response) {
        setBillingSubscription({
          status: response.status,
          trialEnd: response.trialEnd,
          totalMessages: response.usage?.conversations?.used ?? 0,
        });
      }

      updateFormState(false, false, false);
    } catch (e) { }
  }

  function isUserSettingPath() {
    const { pathname } = location;
    return pathname.includes("user-settings") || pathname.includes("account");
  }

  function findRelevantNavigationIndex(navigations, currentPath) {
    // App navigation hrefs are relative to /app/:appId; account hrefs are absolute.
    const navigationPath = currentPath.replace(/^\/app\/[^/]+(?=\/|$)/, "");

    return navigations.findIndex(({ href, children = [] }) =>
      [href, ...children.map(child => child.href)].some(path =>
        navigationPath === path || navigationPath.startsWith(`${path}/`)
      )
    );
  }

  async function setupNavigation() {
    const { pathname } = location;
    let navigation;

    if (appInfo && pathname.includes('app')) {
      navigation = appNavigation.map(item => ({ ...item }));
      if (appInfo.appMode === APP_MODES.PROMPT_BASED) {
        // Prompt-based apps lead with the agent itself, then its sub-agents,
        // before the shared workflow/data/channel entries.
        navigation.unshift(
          { ...AGENT_ROOT_NAV_ITEM },
          { ...SUB_AGENTS_NAV_GROUP }
        );
      }
    } else if (isUserSettingPath()) {
      navigation = userSettingsNavigation;
    } else {
      navigation = appsNavigation;
    }

    setSelectedNav(findRelevantNavigationIndex(navigation, pathname));

    setNavigation([...navigation]);
  }

  const initFetch = () => {
    fetchNotifications();
    fetchMyAccount();
    fetchSubscription();
    fetchReviewRequest();
    setupNavigation();
  };

  useEffect(() => {
    initFetch();
  }, []);

  useEffect(() => {
    console.log('Fetching review request for role:', userRole);
    fetchReviewRequest()
  }, [userRole]);

  useEffect(() => {
    initFetch();
  }, [appInfo, location]);

  const redirectToWebPlugin = async () => {
    // window.open(
    //   `${config.WEB_PLUGIN_URL}?app_id=${appInfo.appId}&test_mode=1`,
    //   "_blank"
    // );
    navigate(`/app/${appInfo.appId}/agent-preview`);
  };

  // Publishing happens on the Versions page; focus its Publish button on arrival.
  const openVersionsToPublish = () => {
    navigate(`/app/${appInfo.appId}/versions`, { state: { focusPublish: true } });
  };

  const handleOpenTextToAgentBuild = () => {
    setOpenTextToAgentBuild(true)
  }

  const handleCloseTextToAgentBuild = () => {
    setOpenTextToAgentBuild(false)
  }

  return (
    <div className="flex h-screen">
      <ImpersonationBanner />
      <ConversationLimitBanner account={account} belowImpersonation={impersonating} />
      {/* Sidebar Component */}

      {openTextToAgentBuild &&
        <TextToAgentModel
          onCompletion={handleCloseTextToAgentBuild}
          onCancel={handleCloseTextToAgentBuild} />
      }

      <Sidebar open={menuOpen} onClose={() => setMenuOpen(false)}>
        <div className="flex h-full flex-col">
          {/* Copilot launcher: above the Build section */}
          {!impersonating && <CopilotLaunch appId={appInfo?.appId ? String(appInfo.appId) : null} />}
          <div className="flex h-full max-h-[75vh] flex-col overflow-y-auto overflow-x-hidden [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
            {groupNavigationBySection(navigation).map(({ section, items }) => (
              <div key={section} className="flex flex-col gap-0.5">
                <SidebarSectionLabel>{section}</SidebarSectionLabel>
                {items.map((item) => {
                  const index = item.index;
                  if (item.children?.length) {
                    return (
                      <MenuItemGroup
                        key={index}
                        active={selectedNav === index}
                        icon={item.icon}
                        name={item.name}
                        tip={item.tip}
                        items={item.children}
                        pathname={location.pathname}
                      />
                    );
                  }
                  return (
                    <MenuItem
                      key={index}
                      active={selectedNav === index}
                      icon={item.icon}
                      name={item.name}
                      href={item.href}
                      tip={item.tip}
                      flash={flashingNav.has(item.href)}
                    />
                  );
                })}
                {section === NAV_SECTIONS.BUILD && appInfo && appInfo.appId && (
                  <>
                    <MenuItem
                      icon={<LuPlayCircle />}
                      name="Simulator"
                      tip="Try the agent in the simulator"
                      active={location.pathname.includes("agent-preview")}
                      onClick={redirectToWebPlugin}
                    />
                    <MenuItem
                      icon={<LuRocket />}
                      name="Publish"
                      tip="Publish a new version of the agent"
                      active={location.pathname.endsWith("/versions")}
                      onClick={openVersionsToPublish}
                      badge={
                        reviewRequestData !== null && userRole === "admin" && (
                          <Tooltip content="Review request pending">
                            <span className="relative flex h-2.5 w-2.5">
                              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
                              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500" />
                            </span>
                          </Tooltip>
                        )
                      }
                    />
                  </>
                )}
              </div>
            ))}
          </div>
          {/* bottom menu-items */}
          <div className="pb-2 mt-auto">
            {appNavigationEnd.map((item, index) => {
              return (
                <MenuItem
                  key={index}
                  icon={item.icon}
                  name={item.name}
                  href={item.href}
                  resetContext={item.resetContext}
                  appPath={false}
                />
              );
            })}
          </div>
        </div>
      </Sidebar>

      {/* Header + Main  */}
      <div className={`relative min-w-0 w-full bg-surface overflow-y-auto transition-[padding] duration-200 motion-reduce:transition-none ${menuOpen ? 'pl-56' : 'pl-0'}`}>
        {!menuOpen && <button type="button" aria-label="Open navigation" aria-expanded={false} aria-controls="main-navigation" onClick={() => setMenuOpen(true)} className="fixed top-2 left-3 z-30 rounded-xl border border-line bg-panel p-2 text-ink/80 shadow-lg hover:bg-raised focus-visible:ring-2 focus-visible:ring-blue-500"><LuMenu size={20} /></button>}
        <Header menuOpen={menuOpen} leftContent={!menuOpen && !isSimulator && !isAppConsole && !copilotPanelOpen && !impersonating && (
          <CopilotLaunch compact appId={appInfo?.appId ? String(appInfo.appId) : null} />
        )}>
          <div className="flex items-center gap-3 pl-2 pr-3">
            <span className="block h-1 w-1 rounded-full bg-gray-300" />
            <a
              className="cursor-pointer text-sm font-medium text-ink/80 transition-colors hover:text-ink"
              href="https://docs.botcircuits.com/"
              target="_blank"
              rel="noreferrer"
            >
              Docs
            </a>
            <span className="block h-1 w-1 rounded-full bg-gray-300" />
          </div>
          <div className="flex items-center gap-2">

            {/*{appInfo && appInfo.appId && (*/}
            {/*    <div className="relative">*/}
            {/*      <IconButton onClick={() => (window.location = "/contact-us")}>*/}
            {/*        <Tooltip content="Build with AI">*/}
            {/*          <SparkIcon size={24} />*/}
            {/*        </Tooltip>*/}
            {/*      </IconButton>*/}
            {/*      /!* Blinking Indigo Dot *!/*/}
            {/*      <div className="absolute top-0 right-0 w-2 h-2 bg-indigo-500 rounded-full animate-ping"></div>*/}
            {/*      <div className="absolute top-0 right-0 w-2 h-2 bg-indigo-500 rounded-full"></div>*/}
            {/*    </div>*/}
            {/*)}*/}

            {/* {appInfo && appInfo.appId && (
                <IconButton onClick={handleOpenTextToAgentBuild}>
                  <Tooltip content="Build Agent with AI">
                    <SparkIcon size={24} />
                  </Tooltip>
                </IconButton>
            )} */}

            <IconButton onClick={() => (window.location = "/contact-us")}>
              <Tooltip content="Contact Us">
                <HiChatBubbleOvalLeftEllipsis size={24} />
              </Tooltip>
            </IconButton>

            <IconHref to="/notifications">
              <Tooltip content="Notifications">
                <HiBell size={24} />
              </Tooltip>
            </IconHref>

            {/* Theme button */}

            {/* <Tooltip content="Theme">
              <DarkThemeToggle theme={{
                'root': {
                  'base': 'p-2 hover:bg-raised rounded-md transition-all',
                  'icon': 'text-ink/80 w-6 h-6 hover:text-ink'
                }
              }} />
            </Tooltip> */}
          </div>
        </Header>
        {/* Main Content */}
        <div className="h-full bg-surface pt-14">
          <CopilotWorkspace onPanelOpenChange={handleCopilotPanelOpenChange} onStepperOpenChange={handleCopilotStepperOpenChange}><DashboardRouter /></CopilotWorkspace>
        </div>
      </div>
    </div>
  );
}
